import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, configureApi, setImpersonation, type ImpersonationSession, type Me } from '../api';
import { config } from '../config';
import * as cognito from './cognito';

/** 로컬 메모리 API 의 시드 사용자(x-dev-user). dev 모드에서만 쓰인다. */
export const DEV_USERS = [
  { id: 'u-hq', label: '본사 관리자' },
  { id: 'u-dir', label: '원장님 (햇살 예시)' },
  { id: 'u-th', label: '선생님' },
  { id: 'u-g1', label: '보호자' },
] as const;

const DEV_KEY = 'selfon.devUser';
const IMP_KEY = 'selfon.impersonation';
/** 자녀 모드: 로그인 세션 동안 유지(새로고침해도 학생 화면). 로그아웃하거나 새로 로그인할 때만 풀린다. */
const CHILD_KEY = 'selfon.childMode';
const ss = {
  get: (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string | null) => { try { if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch { /* 저장 불가 환경 */ } },
};

interface AuthState {
  me: Me | null;
  loading: boolean;
  impersonation: ImpersonationSession | null;
  /** 데모·로컬: 로그인 서버가 확인한 사용자 id 로 들어간다 */
  signInDev: (userId: string) => Promise<void>;
  startChildMode: (childId: string) => Promise<void>;
  /** 가입 마무리용: 화면을 바꾸지 않고 API 요청 주체만 정한다(null 이면 해제) */
  setDevUser: (userId: string | null) => void;
  signInCognito: typeof cognito.signIn;
  afterSignIn: () => Promise<void>;
  signOut: (reason?: string) => void;
  /** 탈퇴 마무리: 로그인 수단을 지우고 로그아웃한다 */
  deleteLogin: () => Promise<void>;
  startImpersonation: (s: ImpersonationSession) => Promise<void>;
  endImpersonation: () => Promise<void>;
  notice: string | null;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [impersonation, setImp] = useState<ImpersonationSession | null>(() => {
    const raw = ss.get(IMP_KEY);
    try { return raw ? (JSON.parse(raw) as ImpersonationSession) : null; } catch { return null; }
  });
  const devUser = useRef<string | null>(ss.get(DEV_KEY));
  const childMode = useRef<string | null>(ss.get(CHILD_KEY));
  const setChild = (id: string | null) => { childMode.current = id; ss.set(CHILD_KEY, id); };

  const signOut = useCallback((reason?: string) => {
    if (config.authMode === 'cognito') cognito.signOut();
    devUser.current = null; ss.set(DEV_KEY, null); ss.set(IMP_KEY, null); setChild(null);
    setImpersonation(null); setImp(null); setMe(null);
    setNotice(reason ?? null);
  }, []);

  useEffect(() => {
    configureApi({
      headers: async (): Promise<Record<string, string>> => {
        const child: Record<string, string> = childMode.current ? { 'x-selfon-child-mode': childMode.current } : {};
        if (config.authMode !== 'cognito') return devUser.current ? { 'x-dev-user': devUser.current, ...child } : {};
        const t = await cognito.idToken();
        return t ? { authorization: `Bearer ${t}`, ...child } : {};
      },
      onUnauthorized: () => setMe((cur) => { if (cur) setNotice('로그인이 만료되었습니다. 다시 로그인하세요.'); return null; }),
    });
  }, []);

  const loadMe = useCallback(async () => {
    setImpersonation(impersonation && new Date(impersonation.expiresAt) > new Date() ? impersonation.id : null);
    try { setMe(await api.me()); } catch { setMe(null); }
  }, [impersonation]);

  useEffect(() => {
    (async () => {
      const hasSession = config.authMode !== 'cognito' ? Boolean(devUser.current) : Boolean(await cognito.idToken());
      if (hasSession) await loadMe();
      setLoading(false);
    })();
  }, [loadMe]);

  // 무조작 자동 로그아웃
  useEffect(() => {
    if (!me) return;
    let timer = window.setTimeout(fire, config.idleMinutes * 60_000);
    function fire() { signOut(`${config.idleMinutes}분 동안 사용하지 않아 자동으로 로그아웃했습니다.`); }
    const reset = () => { window.clearTimeout(timer); timer = window.setTimeout(fire, config.idleMinutes * 60_000); };
    const evs = ['pointerdown', 'keydown'] as const;
    evs.forEach((e) => window.addEventListener(e, reset));
    return () => { window.clearTimeout(timer); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [me, signOut]);

  // 대리접속 만료 시 자동 종료
  useEffect(() => {
    if (!impersonation) return;
    const ms = new Date(impersonation.expiresAt).getTime() - Date.now();
    const t = window.setTimeout(() => {
      ss.set(IMP_KEY, null); setImpersonation(null); setImp(null);
      setNotice('대리접속 시간이 끝나 본사 화면으로 돌아왔습니다.');
    }, Math.max(0, ms));
    return () => window.clearTimeout(t);
  }, [impersonation]);

  const value = useMemo<AuthState>(() => ({
    me, loading, impersonation, notice,
    signInDev: async (userId) => { devUser.current = userId; ss.set(DEV_KEY, userId); setChild(null); setNotice(null); await loadMe(); },
    setDevUser: (userId) => { devUser.current = userId; ss.set(DEV_KEY, null); },
    startChildMode: async (childId) => { await api.startChildMode(childId); setChild(childId); await loadMe(); },
    signInCognito: cognito.signIn,
    afterSignIn: async () => { setChild(null); setNotice(null); await loadMe(); },
    signOut,
    deleteLogin: async () => {
      if (config.authMode === 'cognito') await cognito.deleteCurrentUser();
      signOut('탈퇴가 완료되었습니다.');
    },
    startImpersonation: async (s) => { ss.set(IMP_KEY, JSON.stringify(s)); setImp(s); },
    endImpersonation: async () => {
      if (impersonation) { try { await api.endImpersonation(impersonation.id); } catch { /* 만료됐어도 화면은 복귀 */ } }
      ss.set(IMP_KEY, null); setImpersonation(null); setImp(null);
    },
  }), [me, loading, impersonation, notice, loadMe, signOut]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('AuthProvider 밖에서 useAuth 를 쓸 수 없습니다.');
  return v;
}
